import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '@supabase/auth-helpers-react';
import { useQuery } from '@tanstack/react-query';
import { getUserCourses, getUserData } from '../api/supabaseAPI';
import CourseDetailModal from '../components/CourseDetailModal';
import '../styles/Layout.css';
import '../styles/Practice.css';
import '../styles/Courses.css';
import '../styles/Introduction.css';

export default function Courses() {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const userId = session?.user?.id;

  const { data: courses = [], isLoading, isError } = useQuery({
    queryKey: ['courses', userId],
    queryFn: () => getUserCourses(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    initialData: location.state?.courses, // Use passed data as initial cache
  });

  const { data: userData } = useQuery({
    queryKey: ['userData', userId],
    queryFn: () => getUserData(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  const handleStartPractice = () => {
    navigate('/practice');
  };

  const handleStartCustomPractice = () => {
    navigate('/practice/custom');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' });
  };

  const parseCourseCount = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const parsed = parseInt(String(value), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getCourseProgress = (course) => {
    const total = parseCourseCount(course.content_count ?? course.length);
    const rawIndex = Number.isFinite(Number(course.current_index)) ? Number(course.current_index) : 0;
    const safeIndex = Math.max(0, rawIndex);
    const hasStatus = !!course.status;
    const isCompleted = course.status?.is_completed || false;
    const statusKey = isCompleted ? 'completed' : !hasStatus ? 'notStarted' : 'inProgress';
    const statusClass = isCompleted ? 'status-completed' : !hasStatus ? 'status-not-started' : 'status-in-progress';
    return { total, safeIndex, statusKey, statusClass };
  };

  const currentLanguage = userData?.settings?.language;
  const courseStatusPriority = {
    inProgress: 0,
    notStarted: 1,
    completed: 2,
  };

  const displayedCourses = (currentLanguage
    ? courses.filter((course) => !course.language || course.language === currentLanguage)
    : courses
  ).slice().sort((courseA, courseB) => {
    const progressA = getCourseProgress(courseA);
    const progressB = getCourseProgress(courseB);
    const priorityA = courseStatusPriority[progressA.statusKey] ?? Number.MAX_SAFE_INTEGER;
    const priorityB = courseStatusPriority[progressB.statusKey] ?? Number.MAX_SAFE_INTEGER;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    const timeA = courseA.generated_at ? new Date(courseA.generated_at).getTime() : 0;
    const timeB = courseB.generated_at ? new Date(courseB.generated_at).getTime() : 0;
    return timeB - timeA;
  });

  return (
    <main className="main-content width-practice">
      <h1 className="page-title">{t('coursesPage.title')}</h1>

      {isLoading ? (
        <div className="spinner"></div>
      ) : (
        <div className="courses-page-container">
          {/* Practices Section */}
          <section className="courses-section">
            <h3 className="courses-section-subtitle">{t('coursesPage.practicesTitle')}</h3>
            <div className="cards-grid">
              <div className="diagnosis-container daily-practice-card course-card-item">
                <div className="course-card-shell">
                  <div className="course-card-topline">
                    <span className="course-card-kicker">{t('coursesPage.dailyFlow')}</span>
                    <span className="course-card-status-pill practice-ready">{t('coursesPage.ready')}</span>
                  </div>
                </div>
                <div className="card-content">
                    <h3 className="daily-practice-title">{t('coursesPage.dailyPracticeTitle')}</h3>
                    <p className="daily-practice-description">
                    {t('coursesPage.dailyPracticeDescription')}
                    </p>
                </div>
                <div className="card-actions">
                    <button className="practice-btn primary" onClick={handleStartPractice}>
                    {t('coursesPage.goToPractice')}
                    </button>
                </div>
              </div>
              <div className="diagnosis-container daily-practice-card course-card-item">
                <div className="course-card-shell">
                  <div className="course-card-topline">
                    <span className="course-card-kicker">{t('coursesPage.quickFlow')}</span>
                    <span className="course-card-status-pill practice-ready">{t('coursesPage.preview')}</span>
                  </div>
                </div>
                <div className="card-content">
                    <h3 className="daily-practice-title">{t('coursesPage.customPracticeTitle')}</h3>
                    <p className="daily-practice-description">
                    {t('coursesPage.customPracticeDescription')}
                    </p>
                </div>
                <div className="card-actions">
                    <button className="practice-btn primary" onClick={handleStartCustomPractice}>
                    {t('coursesPage.openCustomPractice')}
                    </button>
                </div>
              </div>
            </div>
          </section>

          {/* Courses Section */}
          <section className="courses-section">
            <h3 className="courses-section-subtitle">{t('coursesPage.coursesTitle')}</h3>
            <div className="cards-grid">
                {displayedCourses.length > 0 ? (
                    displayedCourses.map((course, index) => (
                    <div
                      key={index} 
                      className={`diagnosis-container course-card-item ${getCourseProgress(course).statusClass}`}
                      onClick={() => setSelectedCourse(course)}
                    >
                        {(() => {
                          const { statusKey, statusClass, total, safeIndex } = getCourseProgress(course);
                          return (
                            <>
                        <div className="course-card-shell">
                          <div className="course-card-topline">
                            <span className="course-card-kicker">{t('coursesPage.courseKicker')}</span>
                            <span className={`course-card-status-pill ${statusClass}`}>{t(`coursesPage.status.${statusKey}`)}</span>
                          </div>
                        </div>
                        <div className="card-content">
                            <h3 className="course-card-title">{course.title || t('coursesPage.courseFallbackTitle', { number: index + 1 })}</h3>
                            <p className="course-card-description">
                            <b>{t('coursesPage.descriptionLabel')} </b>{course.description || t('coursesPage.noDescription')}
                            </p>
                        </div>
                        <div className="course-topics-wrapper">
                            {course.topics && course.topics.map((topic, i) => (
                                <span key={i} className="course-topic-tag">
                                    {topic}
                                </span>
                            ))}
                        </div>
                        <div className="course-card-footer">
                          <div className="course-card-progress-meta">
                            <span className="course-card-progress-label">{t('coursesPage.progress')}</span>
                            <strong className="course-card-progress-value">
                              {total > 0 ? Math.min(safeIndex + 1, total) : 0} / {total}
                            </strong>
                          </div>
                          {course.generated_at && (
                            <span className="course-date-text">{formatDate(course.generated_at)}</span>
                          )}
                        </div>
                            </>
                          );
                        })()}
                    </div>
                    ))
                ) : (
                    <div className="diagnosis-container no-courses-message course-card-item">
                        <p>{t('coursesPage.noCoursesMessage')}</p>
                    </div>
                )}
            </div>
          </section>
        </div>
      )}
      {selectedCourse && (
        <CourseDetailModal 
          course={selectedCourse} 
          onClose={() => setSelectedCourse(null)} 
        />
      )}
    </main>
  );
}
